import { LoginSectionHandle } from "@/components/molecules/Modals/SpecificModals/LoginSection";
import BottomSheet from "@/design-system/BottomSheet/BottomSheet";
import { Button } from "@/design-system/Button/Button";
import BackButton from "@/design-system/SpecificButtons/BackButton/BackButton";
import CloseButton from "@/design-system/SpecificButtons/CloseButton/CloseButton";
import { CHOOSE_CLUB_COUNTRY_SIGNUP_CONTEXT } from "@/events/frontPageMixpanel";
import { storeSignupContext } from "@/events/signupTracking";
import { useScrollLock } from "@/hooks/useScrollLock";
import {
  FavoriteType,
  favoritesFlowSelector,
  resetFavoritesFlow,
  setFavoritesStep,
  setPickerType,
} from "@/redux/frontPage/frontPageSlice";
import { addAlert } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import {
  updateUser,
  userFavoriteClubIdsSelector,
  userFavoriteCountryIdSelector,
  userIsLoggedInSelector,
} from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { ArrowRight } from "lucide-react";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { FavoritePickerPanel } from "./FavoritePickerPanel";
import { FavoriteStepper } from "./FavoriteStepper";
import { LoginStep } from "./LoginStep";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const FavoritesSheet = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation(["frontPage", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const { step, pickerType, firstChosen, selectedClubIds, selectedCountryId } =
    useSelector(favoritesFlowSelector);
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const savedClubIds = useSelector(userFavoriteClubIdsSelector);
  const savedCountryId = useSelector(userFavoriteCountryIdSelector) ?? null;

  useScrollLock(isOpen);

  const loginRef = useRef<LoginSectionHandle | null>(null);
  const [loginSubScreen, setLoginSubScreen] = useState<"email" | "code">(
    "email",
  );

  const order: FavoriteType[] =
    firstChosen === "country" ? ["country", "club"] : ["club", "country"];
  const isStep2 = step === "picker" && pickerType === order[1];
  const isOnLoginCodeScreen = step === "login" && loginSubScreen === "code";
  const canGoBack = step === "login" || isStep2 || isOnLoginCodeScreen;

  const hasSelection = selectedClubIds.length > 0 || selectedCountryId != null;

  // Opening the sheet seeds the flow from the saved favorites, so a run through
  // the picker that touches nothing must not fire an update.
  const isUnchanged =
    selectedCountryId === savedCountryId &&
    selectedClubIds.length === savedClubIds.length &&
    selectedClubIds.every((id) => savedClubIds.includes(id));

  const handleClose = () => {
    dispatch(resetFavoritesFlow());
    setLoginSubScreen("email");
    onClose();
  };

  const handleBack = () => {
    if (isOnLoginCodeScreen) {
      loginRef.current?.goToEmailScreen();
      return;
    }
    if (step === "login") {
      dispatch(setFavoritesStep("picker"));
      return;
    }
    if (isStep2) dispatch(setPickerType(order[0]));
  };

  const persist = async () => {
    if (!hasSelection || isUnchanged) {
      handleClose();
      return;
    }
    if (!isLoggedIn) {
      // Attribute the signup to this gate: forwarded as x-mp-signup-* headers,
      // the backend fires user_signed_up with context=choose_club_country on
      // first-time login. This gate renders LoginSection directly rather than
      // going through messageSlice.showModal, so nothing stores it for us.
      storeSignupContext(CHOOSE_CLUB_COUNTRY_SIGNUP_CONTEXT, {
        section: "Front Page",
        subsection: "For You",
      });
      dispatch(setFavoritesStep("login"));
      return;
    }
    try {
      await dispatch(
        updateUser({
          favoriteClubs: selectedClubIds,
          favoriteCountries: selectedCountryId ? [selectedCountryId] : [],
        }),
      ).unwrap();
      dispatch(
        addAlert({
          description: t("favorites.saved"),
          type: "success",
        }),
      );
      handleClose();
    } catch {
      dispatch(
        addAlert({
          description: t("favorites.saveFailed"),
          type: "error",
        }),
      );
    }
  };

  const handlePrimary = () => {
    if (isStep2) {
      persist();
    } else {
      // Step 1 "Save →": advance to the optional second type.
      dispatch(setPickerType(order[1]));
    }
  };

  // Step 1 Skip: jump to the second type without picking here. Last step: commit.
  const handleSkip = () => {
    if (isStep2) {
      persist();
    } else {
      dispatch(setPickerType(order[1]));
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      className="h-dvh mobile:h-[90vh] max-w-full overflow-y-auto bg-neutral-900"
    >
      <div className="mx-auto flex max-w-content flex-col gap-4 px-4 py-4 large-desktop:px-16">
        <div className="flex items-center justify-between">
          <div className="w-[30px]">
            {canGoBack && <BackButton onClick={handleBack} />}
          </div>
          <CloseButton onClick={handleClose} />
        </div>

        {step === "login" ? (
          <LoginStep
            loginRef={loginRef}
            onScreenChange={setLoginSubScreen}
            onLoginSuccess={async () => {
              await dispatch(
                updateUser({
                  favoriteClubs: selectedClubIds,
                  favoriteCountries: selectedCountryId
                    ? [selectedCountryId]
                    : [],
                }),
              );
              dispatch(
                addAlert({
                  description: t("favorites.saved"),
                  type: "success",
                }),
              );
              handleClose();
            }}
          />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-y-2">
              <h1 className="text-h1">{t("favorites.title")}</h1>
              <span className="max-w-xl text-sm text-neutral-500">
                {t("forYou.intro")}
              </span>
            </div>
            <FavoriteStepper />
            <FavoritePickerPanel type={pickerType} />

            <div className="flex items-center justify-between pt-2">
              <Button variant="secondary" size="medium" onClick={handleSkip}>
                {t("favorites.skip")}
              </Button>
              <Button
                variant="primary"
                size="medium"
                disabled={isStep2 && !hasSelection}
                onClick={handlePrimary}
                icon={isStep2 ? undefined : <ArrowRight size={18} />}
                iconPosition="right"
              >
                {isStep2 ? t("favorites.saveFavorites") : t("common:save")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </BottomSheet>
  );
};
